/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';
import {
	GET_RESOURCES_CONFIG_STATUS,
	TOGGLE_RESOURCES_ENABLEMENT,
	ToggleResourcesPayload,
	ResourceConfiguration,
	ResourceConfigurationResponse,
	NotificationSettings,
	RolePermissions
} from './types';

// API endpoint name as configured in index.tsx
const API_NAME = 'revisedResources';

/**
 * Toggle the resources enablement status
 * @param companyId - Company identifier
 * @param payload - Object containing the enabled status
 * @returns The response from the API
 */
export const toggleResourcesEnablement = async (companyId: string, payload: ToggleResourcesPayload): Promise<any> =>
{
	try
	{
		const response = await API.patch(
			API_NAME,
			`/configurations/${companyId}/toggleResources`,
			{
				body : payload
			}
		);

		return response;
	}
	catch (error)
	{
		console.error('Error toggling resources enablement:', error);
		throw error;
	}
};

/**
 * Get the current resource configuration status
 * @param companyId - Company identifier
 * @returns The current status of resources configuration including content types
 */
export const getResourcesConfigurationStatus = async (companyId: string): Promise<ResourceConfiguration> =>
{
	try
	{
		const response = await API.get(
			API_NAME,
			`/configurations/${companyId}`,
			{}
		);

		// Default configuration if the API response is incomplete
		const defaultConfig: ResourceConfiguration = {
			enabled        : false,
			modulesEnabled : {
				articles  : false,
				videos    : false,
				podcasts  : false,
				documents : false,
				images    : false
			},
			creationFeatures : {
				playlist : false,
				season   : false
			},
			uploadSettings : {
				sources : {
					local        : true,
					mediaDrive   : true,
					embeddedLink : true,
					resources    : true
				},
				maxFileSizeMB : 250
			},
			accessSettings : {
				public   : true,
				private  : true,
				download : true,
				share    : true
			},
			notificationSettings : {
				superAdmin : {
					create    : true,
					edit      : true,
					delete    : true,
					publish   : true,
					unpublish : true
				},
				admin : {
					create    : true,
					edit      : true,
					delete    : true,
					publish   : true,
					unpublish : true
				}
			},
			rolePermissions : {
				superAdmin : {},
				admin      : {},
				user       : {}
			}
		};

		// If no data is returned, return the default configuration
		if (!response || !response.data)
		{
			return defaultConfig;
		}

		// Extract data from response and merge with defaults
		const result: ResourceConfiguration = {
			...defaultConfig,
			enabled              : response.data.resources?.enabled ?? defaultConfig.enabled,
			modulesEnabled       : response.data.modulesEnabled ?? defaultConfig.modulesEnabled,
			creationFeatures     : response.data.creationFeatures ?? defaultConfig.creationFeatures,
			uploadSettings       : response.data.uploadSettings ?? defaultConfig.uploadSettings,
			accessSettings       : response.data.accessSettings ?? defaultConfig.accessSettings,
			notificationSettings : response.data.notificationSettings ?? defaultConfig.notificationSettings,
			rolePermissions      : response.data.rolePermissions ?? defaultConfig.rolePermissions
		};

		return result;
	}
	catch (error)
	{
		console.error('Error fetching resources status:', error);
		throw error;
	}
};